import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const PolitiqueConfidentialite = () => {
  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Légal
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Politique de Confidentialité
            </h1>
            <p className="text-xl text-gray-400">
              Protection de vos données personnelles
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">

          {/* Introduction */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">Introduction</h2>
            <p className="text-gray-400 leading-relaxed">
              FlowKraft Agency s'engage à protéger la confidentialité et la sécurité de vos données personnelles.
              Cette politique de confidentialité explique comment nous collectons, utilisons, partageons et
              protégeons vos informations personnelles conformément au Règlement Général sur la Protection
              des Données (RGPD) et à la loi "Informatique et Libertés".
            </p>
          </div>

          {/* Responsable du traitement */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">1. Responsable du traitement des données</h2>
            <div className="text-gray-400 space-y-2">
              <p><span className="text-gray-300">Responsable :</span> FlowKraft Agency</p>
              <p><span className="text-gray-300">Adresse :</span> Toulouse, France</p>
              <p><span className="text-gray-300">Email :</span> <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-secondary hover:underline">info@flowkraftagency.com</a></p>
              <p><span className="text-gray-300">Téléphone :</span> <a href="tel:+33652794562" className="text-flowkraft-secondary hover:underline">+33 (0)6 52 79 45 62</a></p>
            </div>
          </div>

          {/* Données collectées */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">2. Données personnelles collectées</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Nous pouvons collecter les types de données personnelles suivants :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données d'identification :</span> nom, prénom, adresse email</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données de contact :</span> numéro de téléphone, adresse postale</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données professionnelles :</span> nom de l'entreprise, fonction, secteur d'activité</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données de navigation :</span> adresse IP, type de navigateur, pages visitées, durée de visite</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données de communication :</span> messages envoyés via nos formulaires de contact</span>
              </li>
            </ul>
          </div>

          {/* Finalités */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">3. Finalités du traitement</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Vos données personnelles sont collectées et traitées pour les finalités suivantes :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Répondre à vos demandes de contact et d'information
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Gérer la relation client et le suivi de projets
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Établir des devis et des contrats
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Améliorer nos services et votre expérience utilisateur
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Vous envoyer des informations sur nos services (avec votre consentement)
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Respecter nos obligations légales et réglementaires
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Analyser et optimiser notre site web
              </li>
            </ul>
          </div>

          {/* Base légale */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">4. Base légale du traitement</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le traitement de vos données personnelles repose sur les bases légales suivantes :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Exécution d'un contrat :</span> pour gérer votre commande ou prestation</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Consentement :</span> pour l'envoi de communications marketing</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Intérêt légitime :</span> pour améliorer nos services et assurer la sécurité de notre site</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Obligation légale :</span> pour respecter nos obligations comptables et fiscales</span>
              </li>
            </ul>
          </div>

          {/* Durée de conservation */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">5. Durée de conservation des données</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Vos données personnelles sont conservées pour la durée nécessaire aux finalités pour lesquelles
              elles sont traitées :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données clients :</span> pendant la durée de la relation contractuelle et jusqu'à 3 ans après la fin de celle-ci</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données prospects :</span> pendant 3 ans à compter du dernier contact</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Données comptables :</span> 10 ans conformément aux obligations légales</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Cookies et données de navigation :</span> 13 mois maximum</span>
              </li>
            </ul>
          </div>

          {/* Destinataires */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">6. Destinataires des données</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Vos données personnelles peuvent être transmises aux catégories de destinataires suivantes :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Personnel autorisé de FlowKraft Agency
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Prestataires techniques (hébergement, maintenance, développement)
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Services de paiement sécurisé
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Autorités légales et réglementaires sur demande
              </li>
            </ul>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Nous nous assurons que tous nos partenaires respectent la confidentialité et la sécurité de vos données.
            </p>
          </div>

          {/* Transferts */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">7. Transferts de données hors UE</h2>
            <p className="text-gray-400 leading-relaxed">
              Vos données peuvent être transférées vers des pays situés hors de l'Union Européenne, notamment
              pour l'hébergement de notre site (Vercel, États-Unis). Ces transferts sont encadrés par des garanties
              appropriées conformément au RGPD (clauses contractuelles types, Privacy Shield, etc.).
            </p>
          </div>

          {/* Sécurité */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">8. Sécurité des données</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger
              vos données personnelles contre :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                L'accès non autorisé
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                La perte, la destruction ou l'altération accidentelle
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                La divulgation ou l'utilisation illicite
              </li>
            </ul>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Ces mesures incluent notamment le chiffrement SSL/TLS, des sauvegardes régulières, des contrôles
              d'accès stricts et la sensibilisation de nos équipes.
            </p>
          </div>

          {/* Droits */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">9. Vos droits</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit d'accès :</span> obtenir une copie de vos données personnelles</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit de rectification :</span> corriger vos données inexactes ou incomplètes</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit à l'effacement :</span> demander la suppression de vos données</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit à la limitation :</span> limiter le traitement de vos données</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit d'opposition :</span> vous opposer au traitement de vos données</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit à la portabilité :</span> recevoir vos données dans un format structuré</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit de retirer votre consentement :</span> à tout moment</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Droit de définir des directives :</span> concernant le sort de vos données après votre décès</span>
              </li>
            </ul>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Pour exercer vos droits, contactez-nous à :{" "}
              <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-secondary hover:underline">
                info@flowkraftagency.com
              </a>
            </p>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale
              de l'Informatique et des Libertés) si vous estimez que vos droits ne sont pas respectés.
            </p>
          </div>

          {/* Cookies */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">10. Cookies et technologies similaires</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Notre site utilise des cookies pour améliorer votre expérience de navigation. Les cookies sont
              de petits fichiers texte stockés sur votre appareil.
            </p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              <span className="text-gray-300">Types de cookies utilisés :</span>
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Cookies essentiels :</span> nécessaires au fonctionnement du site</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Cookies analytiques :</span> pour comprendre comment vous utilisez notre site</span>
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                <span><span className="text-gray-300">Cookies de performance :</span> pour améliorer les performances du site</span>
              </li>
            </ul>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Vous pouvez gérer vos préférences en matière de cookies via les paramètres de votre navigateur.
            </p>
          </div>

          {/* Modifications */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">11. Modifications de la politique</h2>
            <p className="text-gray-400 leading-relaxed">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment.
              Toute modification sera publiée sur cette page avec une date de mise à jour actualisée.
              Nous vous encourageons à consulter régulièrement cette page pour prendre connaissance
              des éventuelles modifications.
            </p>
          </div>

          {/* Contact */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">12. Contact</h2>
            <p className="text-gray-400 leading-relaxed">
              Pour toute question concernant cette politique de confidentialité ou le traitement de vos données
              personnelles, vous pouvez nous contacter :
            </p>
            <div className="text-gray-400 space-y-2 mt-4">
              <p><span className="text-gray-300">Email :</span> <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-secondary hover:underline">info@flowkraftagency.com</a></p>
              <p><span className="text-gray-300">Téléphone :</span> <a href="tel:+33652794562" className="text-flowkraft-secondary hover:underline">+33 (0)6 52 79 45 62</a></p>
              <p><span className="text-gray-300">Adresse :</span> Toulouse, France</p>
            </div>
          </div>

          {/* Date de mise à jour */}
          <div className="text-gray-500 text-sm border-t border-white/10 pt-8">
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/2149755776.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Vos données sont en sécurité
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Nous nous engageons à protéger votre vie privée
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Nous contacter
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
